import { Chart, Filler, type ChartOptions, type TooltipItem } from "chart.js";
import { cloneDeep, compact, orderBy, sortBy } from "lodash";
import moment from "moment";

import {
  TileType,
  type Level,
  type TileDescriptor,
} from "@features/overview-v2/types";
import {
  COMPARE_METRIC_1_LINE_DASH,
  createCompare1BarChartPattern,
  generateDateLabels,
} from "@features/sales-v3/utils/chart";
import { DesignElementManager } from "@hooks/use-design-element-manager";
import { type User } from "@internal/user";
import {
  type GetOverviewMetricsReport_me_overviewMetricsReport,
  type GetOverviewMetricsReport_me_overviewMetricsReport_rows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  PLATFORM_NORTHBEAM,
  convertStartDateToTimeZone,
  metrics,
  type CurrencyCode,
  type DateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { spaceColorScheme } from "@pages/objects/label-colors";

import { chartTooltip } from "./chart-tooltip";

Chart.register(Filler);

export interface DataPoint {
  x: Date;
  y: number | null;
  dateRange: { startDate: string; endDate: string };
}

export interface LegendItem {
  label: string;
  value: number;
  color: string;
  breakdowns: Record<string, string>[];
}

export const LEVEL_TO_NUMBER: Record<string, number> = {
  ad: 1,
  adset: 2,
  campaign: 3,
} as const;

export const NUMBER_TO_LEVEL: Record<number, Level> = {
  1: "ad",
  2: "adset",
  3: "campaign",
} as const;

const PLATFORM_KEY_NAME = "Platform (Northbeam)";
const CONVERSION_LAG_DIMENSION_NAME = "Conversion lag";
const CONVERSION_LAG_ATTRIBUTION_WINDOW_ORDER = {
  "1d": 0,
  "3d": 1,
  "7d": 2,
  "14d": 3,
  "30d": 4,
  "60d": 5,
  "90d": 6,
  LTV: 7,
} as const;

function sortDatasets(
  tileType: TileType,
  metric: SalesPageMetric,
  rows: GetOverviewMetricsReport_me_overviewMetricsReport_rows[],
) {
  if (tileType === TileType.CONVERSION_LAG) {
    return orderBy(
      rows,
      (row) =>
        CONVERSION_LAG_ATTRIBUTION_WINDOW_ORDER[
          row.dimensions[
            CONVERSION_LAG_DIMENSION_NAME
          ] as keyof typeof CONVERSION_LAG_ATTRIBUTION_WINDOW_ORDER
        ],
      metric.isPositiveChangeGood ? "asc" : "desc",
    );
  } else {
    return rows;
  }
}

export function generateChartData({
  tileType,
  metric1,
  metric2,
  metric1Data,
  metric2Data,
  dateRange,
  timezone,
  timeGranularity,
  breakdowns,
}: {
  tileType: TileType;
  metric1: SalesPageMetric;
  metric2?: SalesPageMetric;
  metric1Data: GetOverviewMetricsReport_me_overviewMetricsReport;
  metric2Data?: GetOverviewMetricsReport_me_overviewMetricsReport;
  dateRange: DateInterval;
  timezone: User["timezone"];
  timeGranularity: ITimeGranularityV2;
  breakdowns: string[];
}) {
  const isMultiMetric = !!metric2;
  const startDate = dateRange.startDate;
  const endDate = dateRange.endDate;
  const today = moment().startOf("day");

  const labels = generateDateLabels(
    { startDate, endDate },
    timezone,
    timeGranularity,
  );
  const datasets = [];

  for (const [metric, data] of [
    [metric1, metric1Data],
    [metric2, metric2Data],
  ] as const) {
    if (!metric || !data) {
      continue;
    }

    const rows = sortDatasets(tileType, metric, data.rows);
    const stackValues: Record<string, number> = {};

    const dimensionMapping =
      tileType === TileType.CONVERSION_LAG ? ["Conversion lag"] : breakdowns;

    for (const row of rows) {
      const dataset: {
        data: DataPoint[];
        dimensions: Record<string, string>;
        name: string;
        metric: SalesPageMetric;
      } = {
        data: [],
        dimensions: row.dimensions,
        name: isMultiMetric
          ? metric.metadata.overviewPageMetricName ||
            metric.metadata.name ||
            metric.name
          : dimensionMapping.map((e) => row.dimensions[e]).join(" / "),
        metric: metric,
      };

      for (const dailyMetrics of row.metrics) {
        const date = moment(
          convertStartDateToTimeZone(dailyMetrics.date, timezone),
        );

        const dateString = date.format();
        let value = 0;

        if (metric.baseMetrics) {
          const agg: metrics.MetricPoint = {} as metrics.MetricPoint;

          metric.baseMetrics.forEach((m) => {
            // TODO: Incorporate typeguard on key.
            agg[m.id as Exclude<keyof metrics.MetricPoint, "convs" | "goals">] =
              m.calculate(dailyMetrics);
          });
          value = metric.calculate(agg);
        } else {
          value = metric.calculate(dailyMetrics);
        }

        let y = value;
        // Fixes edge case dividing by 0
        if (value === Infinity || Number.isNaN(value)) {
          y = NaN;
        } else if (Math.abs(y) < 1e-6) {
          y = 0;
        }

        if (tileType === TileType.CONVERSION_LAG) {
          let adjustedValue = y - (stackValues[dateString] ?? 0);
          if (Math.abs(adjustedValue) < 1e-6) {
            adjustedValue = 0;
          }

          stackValues[dateString] = y;
          y = adjustedValue;
        }

        dataset.data.push({
          x: date.toDate(),
          y: date.isAfter(today) ? null : y,
          dateRange: dailyMetrics.dateRange,
        });
      }

      datasets.push(dataset);
    }
  }

  return { labels: labels, datasets };
}

type GetChartOptonsParams = {
  code: CurrencyCode;
  tileType: TileType;
  metric1: SalesPageMetric;
  metric2?: SalesPageMetric;
  alignAxes?: boolean;
  data: ReturnType<typeof generateChartDataset>;
  timeGranularity: ITimeGranularityV2;
  globalDateRange: DateInterval;
};

export function getChartOptions({
  code,
  tileType,
  metric1,
  metric2,
  alignAxes,
  data,
  timeGranularity,
  globalDateRange,
}: GetChartOptonsParams): ChartOptions<any> {
  const isLineChart = tileType === TileType.LINE_CHART;

  const maxYValue = Math.max(
    ...compact(data.datasets.flatMap((e) => e.data).flatMap((e) => e.y)),
  );

  const shouldAlignAxes = alignAxes || metric1.format === metric2?.format;

  return {
    maintainAspectRatio: false,

    parsing: false,

    plugins: {
      tooltip: {
        mode: tileType === TileType.CONVERSION_LAG ? "index" : "nearest",
        enabled: false,
        intersect: true,
        external: chartTooltip,
        itemSort: (a: TooltipItem<"bar">, b: TooltipItem<"bar">) =>
          b.datasetIndex - a.datasetIndex,
        callbacks: {
          title(context: any) {
            const dateRange = context[0].parsed.dateRange;
            let title = "";

            if (timeGranularity === "daily") {
              title = moment(dateRange.startDate).format("dddd, MMMM D");
            }

            if (timeGranularity === "weekly") {
              const localEndDate = moment(dateRange.endDate);
              const globalEndDate = moment(globalDateRange.endDate);
              const endDate = localEndDate.isAfter(globalEndDate)
                ? globalEndDate
                : localEndDate;

              title = `${moment(dateRange.startDate).format(
                "dddd, MMMM D",
              )} - ${moment(endDate).format("dddd, MMMM D")}`;
            }

            if (timeGranularity === "monthly") {
              title = moment(dateRange.startDate).format("MMMM YYYY");
            }

            return title;
          },
          label(context: any) {
            if (tileType === TileType.CONVERSION_LAG) {
              let val = 0;

              for (let i = 0; i < context.datasetIndex + 1; i++) {
                val += context.parsed._stacks.y[i];
              }

              const diff =
                context.datasetIndex === 0
                  ? "-"
                  : context.dataset.metric.formatter(context.parsed.y, code, {
                      precision: "exact",
                    });

              return {
                isConversionLag: true,
                isLineChart: false,
                isMultiMetric: false,
                datasetIndex: context.datasetIndex,
                label: `${context.dataset.name}:`,
                platform: null,
                timeGranularity,
                value: context.dataset.metric.formatter(val, code, {
                  precision: "exact",
                }),
                change: null,
                diff,
                isPositiveChangeGood: false,
              };
            } else {
              const dimensions = context.dataset.dimensions;
              const isPlatform =
                PLATFORM_NORTHBEAM in dimensions &&
                Object.keys(dimensions).length === 1;

              const prev = context.dataset.data[context.dataIndex - 1];
              const curr = context.raw.y;
              let change = null;

              if (prev && curr === 0) {
                change = 0;
              } else if (prev && curr !== 0) {
                change = (curr - prev.y) / prev.y;
              }

              return {
                isConversionLag: false,
                isLineChart: isLineChart,
                isMultiMetric: !!metric2,
                datasetIndex: context.datasetIndex,
                label: context.dataset.name,
                platform: isPlatform ? dimensions[PLATFORM_NORTHBEAM] : null,
                timeGranularity,
                value: context.dataset.metric.formatter(context.raw.y, code, {
                  precision: "approximate",
                }),
                change,
                isPositiveChangeGood:
                  context.dataset.metric.isPositiveChangeGood,
              };
            }
          },
          labelColor(context: any) {
            return { backgroundColor: context.dataset.labelColor };
          },
        },
      },
      legend: {
        display: false,
      },
    },
    barThickness: "flex",
    minBarLength: 3,
    scales: {
      y: {
        stacked: tileType === TileType.CONVERSION_LAG,
        position: "left",
        beginAtZero: true,
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: true,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 12,
          font: {
            size: 11,
          },
          callback: (value: number | string) =>
            metric1.formatter(Number(value), code, {
              precision: "approximate",
            }),
        },
        ...(shouldAlignAxes ? { suggestedMax: maxYValue } : null),
      },
      ...(metric2
        ? {
            y1: {
              position: "right",
              beginAtZero: true,
              grid: {
                drawTicks: false,
                drawBorder: false,
                drawOnChartArea: false,
              },
              ticks: {
                maxTicksLimit: 6,
                padding: 12,
                font: {
                  size: 11,
                },
                callback(value: number | string) {
                  return metric2.formatter(Number(value), code, {
                    precision: "approximate",
                  });
                },
              },
              ...(shouldAlignAxes ? { suggestedMax: maxYValue } : null),
            },
          }
        : null),
      x: {
        stacked: tileType === TileType.CONVERSION_LAG,
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          drawTicks: false,
        },
        ticks: {
          padding: 12,
          maxRotation: 20,
          autoSkip: true,
          font: {
            size: 11,
          },
        },
        type: "timeseries",
        time: {
          unit: (
            {
              hourly: "day",
              daily: "day",
              weekly: "week",
              monthly: "month",
            } as const
          )[timeGranularity],
        },
      },
    },
  } as unknown as ChartOptions<any>;
}

export function generateChartDataset(
  tile: TileDescriptor,
  data: ReturnType<typeof generateChartData>,
  globalDateRange: DateInterval,
) {
  const isLineChart = tile.type === TileType.LINE_CHART;
  const isMultiMetric = !!tile.metric2Id;
  const renderBackdrop = isLineChart && data.datasets.length === 1;
  const datasets = [];

  for (const [i, dataset] of data.datasets.entries()) {
    const singleDataPoint = dataset.data.length === 1;
    const color = getColorForIndexOrPLatform(
      tile.type,
      isMultiMetric ? 0 : i,
      dataset.dimensions,
    );
    const colorTransparent = color + "50";
    const colorBackdrop = color + "20";
    const colorBackdropTransparent = color + "10";
    const today = moment().startOf("day");

    const isIncomplete = (pointDateRange: DateInterval, index: number) => {
      if (
        index === 0 &&
        pointDateRange.startDate !== globalDateRange.startDate
      ) {
        return true;
      }

      if (
        index === dataset.data.length - 1 &&
        pointDateRange.endDate !== globalDateRange.endDate
      ) {
        return true;
      }

      if (moment(pointDateRange.endDate).isSameOrAfter(today)) {
        return true;
      }

      return false;
    };

    let backgroundColor = color;
    let borderWidth: number | number[] = 0;
    let borderDash = undefined;
    let borderColor = color;

    if (isLineChart && isMultiMetric && i === 1) {
      borderDash = COMPARE_METRIC_1_LINE_DASH;
    }

    if (isLineChart) {
      borderWidth = 3;
    } else {
      backgroundColor = dataset.data.map((e, i) =>
        isIncomplete(e.dateRange, i) ? colorTransparent : color,
      );
      borderColor = dataset.data.map((e, i) =>
        isIncomplete(e.dateRange, i) ? colorTransparent : color,
      );

      if (isMultiMetric && i === 1) {
        backgroundColor = dataset.data.map((e, i) =>
          isIncomplete(e.dateRange, i)
            ? createCompare1BarChartPattern(colorTransparent)
            : createCompare1BarChartPattern(color),
        );
        borderColor = dataset.data.map((e, i) =>
          isIncomplete(e.dateRange, i) ? colorTransparent : color,
        );
        borderWidth = 2;
      }
    }

    datasets.push({
      ...dataset,
      id: i,
      yAxisID: isMultiMetric && i > 0 ? `y${i}` : "y",
      pointRadius: singleDataPoint ? 3 : 0,
      pointHitRadius: 50,
      ...(renderBackdrop && !singleDataPoint
        ? { backgroundColor: colorBackdrop, fill: true }
        : { backgroundColor, fill: false }),
      borderColor,
      hoverBackgroundColor: backgroundColor,
      hoverBorderColor: borderColor,
      borderWidth,
      borderDash,
      segment: {
        backgroundColor: (ctx: any) =>
          isIncomplete(ctx.p0.parsed.dateRange, ctx.p0DataIndex) ||
          isIncomplete(ctx.p1.parsed.dateRange, ctx.p1DataIndex)
            ? colorBackdropTransparent
            : colorBackdrop,
        borderColor: (ctx: any) =>
          isIncomplete(ctx.p0.parsed.dateRange, ctx.p0DataIndex) ||
          isIncomplete(ctx.p1.parsed.dateRange, ctx.p1DataIndex)
            ? colorTransparent
            : color,
      },
      labelColor: color,
    });
  }

  return { ...data, datasets };
}

function calculateMetricValue(
  metric: SalesPageMetric,
  rows: GetOverviewMetricsReport_me_overviewMetricsReport_rows[],
) {
  const metricPoint = metrics.newMetricPoint();

  for (const row of rows) {
    for (const value of row.metrics) {
      metrics.addToMetricPoint(metricPoint, value);
    }
  }

  return metric.calculate(metricPoint) ?? 0;
}

export function calculateMetric(
  metric: SalesPageMetric,
  rows: GetOverviewMetricsReport_me_overviewMetricsReport_rows[],
) {
  if (metric.baseMetrics) {
    const agg: metrics.MetricPoint = {} as metrics.MetricPoint;

    metric.baseMetrics.forEach((m) => {
      // TODO: Perform typecheck on key.
      agg[m.id as Exclude<keyof metrics.MetricPoint, "convs" | "goals">] =
        calculateMetricValue(m, rows);
    });
    return metric.calculate(agg);
  } else {
    return calculateMetricValue(metric, rows);
  }
}

export function generateLegendData(
  tile: TileDescriptor,
  metric: SalesPageMetric,
  data: GetOverviewMetricsReport_me_overviewMetricsReport,
): LegendItem[] {
  if (!tile.breakdowns || tile.breakdowns.length === 0) {
    return [];
  }

  const result = [];

  for (const [i, row] of data.rows.entries()) {
    const color = getColorForIndexOrPLatform(tile.type, i, row.dimensions);
    const label = tile.breakdowns.map((e) => row.dimensions[e]).join(" / ");
    const value = calculateMetric(metric, [row]);
    const breakdowns = [];

    for (const breakdown of tile.breakdowns) {
      breakdowns.push({ [breakdown]: row.dimensions[breakdown] });
    }

    result.push({ label, value, color, breakdowns });
  }

  const sortByName = tile.sortBreakdowns === "name";
  const sortFileds = sortByName ? ["label", "value"] : ["value", "label"];
  const sortDirections = sortByName
    ? (["asc", "desc"] as const)
    : (["desc", "asc"] as const);

  return orderBy(result, sortFileds, sortDirections);
}

const designElementManager = new DesignElementManager();

export function getColorForIndexOrPLatform(
  tileType: TileType,
  index: number,
  dimensions: Record<string, string>,
) {
  if (tileType === TileType.CONVERSION_LAG) {
    return spaceColorScheme(1)[index];
  } else {
    let color = designElementManager.getColorForIndex(index);

    if (
      Object.keys(dimensions).length === 1 &&
      PLATFORM_KEY_NAME in dimensions
    ) {
      const platform = dimensions[PLATFORM_KEY_NAME];
      color = designElementManager.getDesignElementColor(platform);
    }

    return color;
  }
}

function metricToExportData(
  tile: TileDescriptor,
  metric: SalesPageMetric,
  metricData: GetOverviewMetricsReport_me_overviewMetricsReport,
  metricName: string,
  timezone: string,
) {
  const dimensionMapping =
    tile.type === TileType.CONVERSION_LAG
      ? ["Conversion lag"]
      : tile.breakdowns;

  const result: Record<string, string>[] = [];

  for (const row of metricData.rows) {
    const dimensions = dimensionMapping
      .map((e) => row.dimensions[e])
      .join(" / ");

    for (const dailyMetrics of row.metrics) {
      const date = moment(
        convertStartDateToTimeZone(dailyMetrics.date, timezone),
      );

      let value = 0;

      if (metric.baseMetrics) {
        const agg: metrics.MetricPoint = {} as metrics.MetricPoint;

        metric.baseMetrics.forEach((m) => {
          agg[m.id as Exclude<keyof metrics.MetricPoint, "convs" | "goals">] =
            m.calculate(dailyMetrics);
        });
        value = metric.calculate(agg);
      } else {
        value = metric.calculate(dailyMetrics);
      }

      value = isNaN(value) ? 0 : value ?? 0;

      if (value < 1e-6) {
        value = 0;
      }

      result.push({
        date: date.format("YYYY-MM-DD"),
        ...(dimensions && {
          [tile.type === TileType.CONVERSION_LAG
            ? "Attribution window"
            : "Channel"]: dimensions,
        }),
        [metricName]: String(value),
      });
    }
  }

  return result;
}

export function dedupMetricNames(
  metric1: SalesPageMetric,
  shouldComputeMetric2Name: boolean,
  metric2?: SalesPageMetric,
) {
  const metric1Name =
    metric1.metadata.overviewPageMetricName ||
    metric1.metadata.name ||
    metric1.name;

  const doesMetric2Exist = shouldComputeMetric2Name && metric2;

  if (!doesMetric2Exist) {
    return { metric1Name, metric2Name: "undefined" };
  }

  const metric2Name = doesMetric2Exist
    ? metric2.metadata.overviewPageMetricName ||
      metric2.metadata.name ||
      metric2.name
    : "undefined";

  if (metric1Name === metric2Name) {
    return {
      metric1Name: `${metric1Name} (1)`,
      metric2Name: `${metric2Name} (2)`,
    };
  } else {
    return { metric1Name, metric2Name };
  }
}

export function tileToExportData({
  tile,
  metric1,
  metric2,
  metric1Data,
  metric2Data,
  timezone,
}: {
  tile: TileDescriptor;
  metric1: SalesPageMetric;
  metric2?: SalesPageMetric;
  metric1Data: GetOverviewMetricsReport_me_overviewMetricsReport;
  metric2Data?: GetOverviewMetricsReport_me_overviewMetricsReport;
  timezone: string;
}) {
  // Forcing this logic into dedupMetricNames to prevent
  // changes causing bugs in the future
  const doesMetric2Exist = Boolean(metric2 && metric2Data);
  const { metric1Name, metric2Name } = dedupMetricNames(
    metric1,
    doesMetric2Exist,
    metric2,
  );

  const metric1ExportData = metricToExportData(
    tile,
    metric1,
    metric1Data,
    metric1Name,
    timezone,
  );

  const result: Record<string, string>[] = [...cloneDeep(metric1ExportData)];

  // Redundancy for typing
  if (doesMetric2Exist && metric2 && metric2Data) {
    const metric2ExportData = metricToExportData(
      tile,
      metric2,
      metric2Data,
      metric2Name,
      timezone,
    );

    for (const result2Row of metric2ExportData) {
      const result1Row = result.find((e) => e.date === result2Row.date);
      if (result1Row) {
        result1Row[metric2Name] = result2Row[metric2Name];
      } else {
        result.push(result2Row);
      }
    }
  }

  return sortBy(result, [
    (e) => moment(e.date),
    (e) => e["Channel"],
    (e) => parseInt(e["Attribution window"]),
  ]);
}
